import Big from 'big.js';
import { Formik } from 'formik';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Button, Buttons, CancelButton, CurrencyInput, Field, Form, Icon, ModalCard, Tooltip } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import useAuth from '~/hooks/useAuth.js';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import { emptyStringToNull, mergeValues } from '~/utils';

export default function ProjectMemberCostRateModal({ project, projectMember, onSaved, onClose }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const auth = useAuth();

  const projectMemberId = projectMember.id;

  const [member, setMember] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.www
          .workspaces(workspace.id)
          .projectSettings(project.id)
          .members(projectMemberId)
          .get();
        setMember(data);
      } catch {
        // Keep the modal in its loading state.
      }
    })();
  }, [api, workspace.id, project.id, projectMemberId]);

  const canEditCost = auth.workspace.manage;

  const defaults = {
    overrideCostRate: false,
    costCurrency: project.currency,
    costPerHour: '',
    overheadCostPerHour: '',
  };
  const initialValues = mergeValues(defaults, _.pick(member ?? {}, Object.keys(defaults)));

  const handleSubmit = async (values, formik) => {
    values = emptyStringToNull(values);

    formik.setSubmitting(true);

    try {
      await api.www.workspaces(workspace.id).projectSettings(project.id).members(projectMemberId).upsert(values);
      toast.success('Project Member saved successfully.');
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(error.message);
      formik.setSubmitting(false);
    }
  };

  return (
    <ModalCard
      title={canEditCost ? 'Edit Cost Rate' : 'View Cost Rate'}
      byline={projectMember.member?.name}
      onClose={onClose}
      data-testid="edit_cost_rate_modal">
      {!member ? (
        <ModalCard.Body>
          <PageLoader />
        </ModalCard.Body>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={Yup.object().shape({
            overrideCostRate: Yup.bool(),
            costCurrency: Yup.string().label('Cost Currency').nullable(),
            costPerHour: Yup.number().label('Labor Cost Per Hour').min(0).max(99999999999).nullable(),
            overheadCostPerHour: Yup.number().label('Overhead Cost Per Hour').min(0).max(99999999999).nullable(),
          })}>
          {({ values, ...formik }) => {
            // The rate fields are always on show, but only live once the member's cost is actually overridden.
            const isReadOnly = !canEditCost || !values.overrideCostRate;

            const totalCostPerHour =
              _.isNumber(values.costPerHour) || _.isNumber(values.overheadCostPerHour)
                ? Big(values.costPerHour || 0)
                    .plus(values.overheadCostPerHour || 0)
                    .toNumber()
                : null;

            return (
              <Form>
                <ModalCard.Body>
                  <Form.Control>
                    <Field.Checkbox
                      label={
                        <div style={{ display: 'flex' }}>
                          Override Cost Rate
                          {['fixed_hourly', 'fixed_monthly'].includes(member.member.costMethodId) && (
                            <Tooltip message="This member has a fixed labor cost, so their cost is based on their availability rather than the hours they work. An overridden cost rate will not change their cost.">
                              <Icon icon="exclamation-triangle" color={colors.warning} spaceLeft />
                            </Tooltip>
                          )}
                        </div>
                      }
                      name="overrideCostRate"
                      disabled={!canEditCost}
                    />
                  </Form.Control>

                  <Form.Control>
                    <Field.WorkspaceCurrencySelect
                      name="costCurrency"
                      placeholder="Cost Currency"
                      clearable={false}
                      disabled={isReadOnly}
                    />
                  </Form.Control>
                  {/* One field per row: three to a row leaves each about 12rem, which truncates every one of
                      these placeholders while the field is still empty. */}
                  <Form.Control>
                    <Field.Currency
                      name="costPerHour"
                      placeholder="Labor Cost Per Hour"
                      precision={7}
                      currency={values.costCurrency}
                      disabled={isReadOnly}
                    />
                  </Form.Control>
                  <Form.Control>
                    <Field.Currency
                      name="overheadCostPerHour"
                      placeholder="Overhead Cost Per Hour"
                      precision={7}
                      currency={values.costCurrency}
                      disabled={isReadOnly}
                    />
                  </Form.Control>
                  <Form.Control>
                    <CurrencyInput
                      placeholder="Total Cost Per Hour"
                      value={totalCostPerHour}
                      currency={values.costCurrency}
                      precision={7}
                      disabled
                      onChange={() => {}}
                    />
                  </Form.Control>
                </ModalCard.Body>

                <ModalCard.Footer>
                  <Buttons align="right">
                    <CancelButton onClick={onClose}>Close</CancelButton>

                    {canEditCost && (
                      <Button isLoading={formik.isSubmitting} onClick={formik.submitForm}>
                        Save &amp; Close
                      </Button>
                    )}
                  </Buttons>
                </ModalCard.Footer>
              </Form>
            );
          }}
        </Formik>
      )}
    </ModalCard>
  );
}
